import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../pages/AuthContext';
import { API_ENDPOINTS, apiRequest } from '../utils/api';

export default function VideoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); // 获取当前登录用户信息
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videos, setVideos] = useState([]);

  // 获取视频详情与评论
  useEffect(() => {
    const fetchVideoDetails = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // 获取所有视频（用于相关推荐）
        const videosResponse = await apiRequest(API_ENDPOINTS.VIDEOS);
        if (!videosResponse.ok) throw new Error('获取视频列表失败');
        const videosData = await videosResponse.json();
        setVideos(videosData);

        // 获取当前视频详情
        const videoResponse = await apiRequest(`${API_ENDPOINTS.VIDEOS}?id=${id}`);
        if (!videoResponse.ok) throw new Error('获取视频详情失败');
        const videoData = await videoResponse.json();
        setSelectedVideo(videoData);

        // 获取评论
        const commentResponse = await apiRequest(`${API_ENDPOINTS.COMMENTS}?videoId=${id}`);
        if (!commentResponse.ok) throw new Error('获取评论失败');
        const commentData = await commentResponse.json();
        setComments(commentData);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchVideoDetails();
    }
  }, [id]);

  // 提交新评论
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      // 使用MySQL兼容的时间格式
      const now = new Date();
      const mysqlTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

      const response = await apiRequest(API_ENDPOINTS.COMMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newComment.trim(),
          videoId: parseInt(selectedVideo.id), // 确保是整数
          timestamp: mysqlTimestamp, // 使用MySQL兼容格式
          author: user ? user.username : '匿名用户', // 使用登录用户名或默认匿名
          author_avatar: user && user.avatar ? user.avatar : `https://picsum.photos/seed/${Math.random()}/200/200` // 添加用户头像或随机头像
        })
      });

      // 检查响应状态
      if (!response.ok) {
        let errorMessage = `HTTP错误: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // 如果无法解析错误响应，则使用响应文本
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            // 忽略文本解析错误
          }
        }
        throw new Error(errorMessage);
      }

      // 解析成功响应
      let newCommentData;
      try {
        newCommentData = await response.json();
        // 添加用户头像信息到返回的数据中
        if (user && user.avatar) {
          newCommentData.author_avatar = user.avatar;
        }
      } catch (parseError) {
        throw new Error('服务器返回了无效的响应格式');
      }

      setComments(prevComments => [newCommentData, ...prevComments]);
      setNewComment('');
    } catch (err) {
      console.error('评论提交错误:', err);
      alert('提交失败：' + err.message);
    }
  };

  // 点赞处理
  const handleLike = async () => {
    try {
      // 先获取当前视频最新数据
      const response = await apiRequest(`${API_ENDPOINTS.VIDEOS}?id=${selectedVideo.id}`);
      if (!response.ok) throw new Error('获取视频详情失败');
      
      const videoData = await response.json();
      
      // 更新点赞数
      const newPraise = (parseInt(videoData.praise) || 0) + 1;
      
      // 发送更新请求
      const updateResponse = await apiRequest(`${API_ENDPOINTS.VIDEOS}?id=${selectedVideo.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          praise: newPraise.toString()
        })
      });

      if (!updateResponse.ok) throw new Error('点赞更新失败');
      
      // 更新本地状态
      setSelectedVideo({
        ...selectedVideo,
        praise: newPraise.toString()
      });
      
    } catch (err) {
      alert('点赞失败：' + err.message);
    }
  };

  // 处理头像图片加载错误的情况
  const handleAvatarError = (e) => {
    // 为匿名用户生成随机头像
    if (e.target.alt === '匿名用户' || !e.target.alt) {
      e.target.src = `https://picsum.photos/seed/${Math.random()}/200/200`;
    } else {
      // 隐藏图片元素并显示SVG图标
      e.target.style.display = 'none';
      e.target.nextSibling.style.display = 'flex';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
        <div className="text-center py-12">
          <button 
            onClick={() => navigate('/')}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  if (!selectedVideo) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-500">视频不存在</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* 视频播放区域 */}
        <div className="lg:w-2/3">
          <div className="aspect-w-16 aspect-h-9 bg-black rounded-lg overflow-hidden">
            <iframe 
              src={selectedVideo.url} 
              high_quality="4" 
              danmaku="1"
              allowfullscreen
              className="w-full h-full"
              sandbox="allow-top-navigation allow-same-origin allow-forms allow-scripts"
              title={`Video player for ${selectedVideo.title}`}
            ></iframe>
          </div>

          <div className="flex items-center text-gray-600 text-sm mb-4">
            {selectedVideo.author_avatar ? (
              <img 
                src={selectedVideo.author_avatar} 
                alt={selectedVideo.author} 
                className="w-10 h-10 rounded-full mr-3" 
                onError={handleAvatarError}
              />
            ) : null}
            <div className="w-10 h-10 rounded-full mr-3 flex items-center justify-center bg-gray-200" 
                 style={{ display: selectedVideo.author_avatar ? 'none' : 'flex' }}>
              <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
              </svg>
            </div>
            <div>
              <p className="font-medium">{selectedVideo.author}</p>
              <p className="text-xs text-gray-500">{selectedVideo.views}次播放 • {selectedVideo.date}</p>
            </div>
            <div className="flex items-center ml-auto space-x-2">
              <button 
                className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-full text-sm"
                onClick={handleLike}
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.583 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 00-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
                赞 {selectedVideo.praise || 0}
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full text-sm"
              >
                关注
              </button>
            </div>
          </div>
          
          {/* 评论区 */}
          <div className="mt-8">
            <h3 className="text-xl font-bold mb-4">评论 ({comments.length})</h3>
            <form onSubmit={handleCommentSubmit} className="mb-6">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="说点什么..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="3"
              />
              <button 
                type="submit"
                className="mt-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
              >
                发送
              </button>
            </form>
            
            {/* 评论列表 */}
            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.id} className="flex space-x-4">
                  <div className="flex-shrink-0">
                    {comment.author_avatar ? (
                      <img 
                        src={comment.author_avatar} 
                        alt={comment.author || '匿名用户'}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={handleAvatarError}
                      />
                    ) : null}
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-200" 
                         style={{ display: comment.author_avatar ? 'none' : 'flex' }}>
                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center mb-2">
                        <span className="font-medium text-gray-900">{comment.author || '匿名用户'}</span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-sm text-gray-500">
                          {new Date(comment.timestamp).toLocaleString('zh-CN')}
                        </span>
                      </div>
                      <p className="text-gray-700">{comment.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* 相关视频推荐 */}
        <div className="lg:w-1/3">
          <h3 className="text-xl font-bold mb-4">相关视频</h3>
          <div className="space-y-4">
            {videos
              .filter(video => video.id !== selectedVideo.id)
              .slice(0, 10)
              .map(video => (
                <div 
                  key={video.id} 
                  className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded transition duration-300"
                  onClick={() => navigate(`/video/${video.id}`)}
                >
                  <div className="relative flex-shrink-0 w-24 h-16 rounded overflow-hidden">
                    <img 
                      src={video.thumbnail || '/img/default_thumbnail.jpg'} 
                      alt={video.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/img/default_thumbnail.jpg';
                      }}
                    />
                    <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                      {video.duration}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 line-clamp-2">{video.title}</h4>
                    <p className="text-xs text-gray-500 mt-1">{video.author}</p>
                    <p className="text-xs text-gray-500">{video.views}次播放</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}